import Datatable from "./datatable_controller";
import flatpickr from "flatpickr";

export default class extends Datatable {
  static targets = ['filter', 'dateRange', 'dateClear']
  // hideCheckedIn: drop the per-order "Checked in" column. It shows one X/Y badge
  // per order, which is meaningless for recurring / multi-day events where check-in
  // is per occurrence (attendance lives on the Attendees tab instead).
  // showAddOns: add an "Add-ons" column (event dashboards whose event has add-on
  // tickets). Must stay in sync with the <th> set and OrderListingDatatable.
  // orgScope: the org-level dashboard recap (many events) runs a lean ~6-column
  // glance — drop Ticket Type + Checked-in and right-align the numeric columns.
  // Must stay in sync with the <th> set (org_scope local) and OrderListingDatatable#org_scope?.
  // recap: the event-overview "Recent Orders" recap glance (.evo-recap look) — a
  // lean 5-column set (Customer / Ticket type / Tickets / Amount / Status) that
  // REPLACES the full column set. Only ever true on the event-overview recap
  // (recap && !orgScope); the org-dashboard recap and both full Orders pages
  // never set it, so they're unchanged. Must stay in sync with the recap <th> set
  // and OrderListingDatatable#recap?.
  static values = { hideCheckedIn: Boolean, showAddOns: Boolean, showExpander: Boolean, orgScope: Boolean, recap: Boolean }

  connect() {
    super.connect();
    $(this.filterTargets).on('select2:select select2:unselect', this.filterSelected.bind(this))

    // Date-range calendar (created_at filter). Stores YYYY-MM-DD start/end and
    // redraws the table; start_date/end_date are sent to the server via manipulateData.
    this.startDate = ''
    this.endDate = ''
    if (this.hasDateRangeTarget) {
      this.flatpickr = flatpickr(this.dateRangeTarget, {
        mode: 'range',
        // Display a friendly range in the (styled) input itself; no altInput so our
        // .order-date-input styling applies directly.
        dateFormat: 'M j, Y',
        onChange: (dates) => {
          this.startDate = dates[0] ? this.fmt(dates[0]) : ''
          this.endDate = dates[1] ? this.fmt(dates[1]) : ''
          if (this.hasDateClearTarget) {
            this.dateClearTarget.classList.toggle('d-none', !this.startDate)
          }
          this.dataTable.draw()
        },
      })
    }

    // Expand/collapse the detail panel when the leading chevron is clicked. The
    // panel HTML is carried on the chevron button's data-order-expander-panel.
    if (this.showExpanderValue) {
      const tableEl = this.tableTarget
      $(tableEl).on('click', '.order-expander', (event) => {
        event.preventDefault()
        const btn = event.currentTarget
        const tr = btn.closest('tr')
        const row = this.dataTable.row(tr)
        if (row.child.isShown()) {
          row.child.hide()
          tr.classList.remove('order-row-expanded')
        } else {
          row.child(btn.dataset.orderExpanderPanel).show()
          tr.classList.add('order-row-expanded')
        }
      })
    }
  }

  filterSelected(_event) {
    this.dataTable.draw()
  }

  manipulateData(data) {
    this.filterTargets.map((filter) => {
      data[filter.name] = $(filter).val()
    })
    data.start_date = this.startDate || ''
    data.end_date = this.endDate || ''
    return data
  }

  fmt(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  clearDates() {
    if (this.flatpickr) this.flatpickr.clear()
    this.startDate = ''
    this.endDate = ''
    if (this.hasDateClearTarget) this.dateClearTarget.classList.add('d-none')
    this.dataTable.draw()
  }

  get columns() {
    // Org-dashboard recap glance: the same lean .evo-recap look as the event
    // recap, plus an Event column (spans many events) and the kebab actions kept.
    // Scoped to recap && orgScope so it only applies to the org DASHBOARD recap —
    // the full Orders page (orgScope, no recap) keeps its existing full table.
    if (this.recapValue && this.orgScopeValue) {
      return [
        { data: "customer" },
        { data: "event" },
        { data: "tickets", className: "num" },
        { data: "amount", className: "num" },
        { data: "status" },
        { data: "actions" },
      ];
    }

    // Event-overview recap glance: a distinct 5-column set, not the full table.
    // Scoped to recap && !orgScope so it can only ever apply to surface 1.
    if (this.recapValue && !this.orgScopeValue) {
      return [
        { data: "customer" },
        { data: "ticket_type" },
        { data: "tickets", className: "num" },
        { data: "amount", className: "num" },
        { data: "status" },
      ];
    }

    // Full column set (the per-event Orders tab). This path is only reached with
    // recap=false, which on this controller means an EVENT object (the full
    // /organizer/orders page is a separate controller, organization-order-
    // datatable). orgScope is only ever set together with recap here, so the
    // org-scope handling lives entirely in the two recap branches above — the
    // old orgScope numeric-class / column-drop branches below were unreachable
    // and were removed.
    const cols = [];
    // Leading expander chevron (event dashboard only). Not orderable/searchable.
    if (this.showExpanderValue) cols.push({ data: "expander", orderable: false, className: "order-expander-col" });
    cols.push(
      { data: "date" },
      { data: "order_id" },
      { data: "full_name" },
      { data: "ticket_type" },
      { data: "tickets_purchased" },
    );
    // Add-ons column sits right after Tickets Purchased when the event has add-ons.
    if (this.showAddOnsValue) cols.push({ data: "add_ons" });
    cols.push(
      { data: "checked_in" },
      { data: "total_amount" },
      { data: "status" },
      { data: "actions" },
    );
    // Must stay in sync with the <th> set rendered in _order_datatable + the Ruby
    // view_columns/data, or DataTables errors on a column with no header.
    let filtered = cols;
    // Checked-in dropped when hidden (recurring/multi-day events).
    if (this.hideCheckedInValue) filtered = filtered.filter((c) => c.data !== "checked_in");
    return filtered;
  }

  get searching() {
    return true;
  }

  get ordering() {
    return false;
  }

  get lengthChange() {
    return false;
  }

}
